import { atom } from 'infrastructure/state/jotai.wrapper'
import { atomWithRefresh } from 'infrastructure/state/atomWithRefresh'
import { fetchUnPaginatedResourceList } from 'shared/api/list'
import { editableResource } from './types'
import { IQueryParam } from 'infrastructure/api'

const resourceKeyInternalAtom = atom('' as ResourceKey)
export const isAddingAtomInternal = atom(false)
const updatingIdAtomInternal = atom(0)

export const updateMapAtom = atom<Record<string, IQueryParam>>({})

export const resourceUpdateMap = {
  users: undefined,
  schools: undefined,
  districts: undefined,
  demos: undefined,
  class: undefined,
  assignmentAttempts: undefined,
  admintools: undefined,
  products: {
    title: 'Products',
    mutationUpdateKey: 'updateProduct',
    mutationAddKey: 'createProduct',
    mutationDeleteKey: 'deleteProduct',
    editableFields: [
      { key: 'title', valueType: 'string' },
      { key: 'partner', setKey: 'partner_id', valueType: 'partner' },
      { key: 'subject_id', valueType: 'subject' },
      { key: 'grade_id', valueType: 'grade' },
      { key: 'Product_Group', valueType: 'productGroup' },
      {
        key: 'Assignable',
        setKey: 'unassignable_at',
        valueType: 'unassignable_at',
      },
    ],
  },
  partners: {
    title: 'Partners',
    mutationUpdateKey: 'updatePartner',
    mutationAddKey: 'createPartner',
    mutationDeleteKey: 'deletePartner',
    editableFields: [{ key: 'name', valueType: 'string' }],
  },
  productGroups: {
    title: 'Product Groups',
    mutationUpdateKey: 'updateGroup',
    mutationAddKey: 'createGroup',
    mutationDeleteKey: 'deleteGroup',
    editableFields: [
      { key: 'name', valueType: 'string' },
      { key: 'description', valueType: 'string' },
      { key: 'products', valueType: 'itemCount' },
    ],
  },
  primaryTrees: {
    title: 'Primary Trees',
    mutationUpdateKey: '',
    mutationAddKey: '',
    mutationDeleteKey: '',
    editableFields: [
      { key: 'tree_id', valueType: 'string' },
      { key: 'version_id', valueType: 'string' },
      { key: 'is_current', valueType: 'string' },
      { key: 'generally_available', valueType: 'string' },
    ],
    disableUpdates: true,
  },
  editableTrees: {
    title: 'Editable Trees',
    mutationUpdateKey: '',
    mutationAddKey: '',
    mutationDeleteKey: '',
    editableFields: [
      { key: 'tree_id', valueType: 'string' },
      { key: 'version_id', valueType: 'string' },
    ],
    disableUpdates: true,
  },
  crosswalkMappingSets: {
    title: 'Crosswalks',
    mutationUpdateKey: 'updateCrosswalkMappingSet',
    mutationAddKey: 'addCrosswalkMappingSet',
    mutationDeleteKey: 'deleteCrosswalkMappingSet',
    editableFields: [
      { key: 'name', valueType: 'string' },
      { key: 'description', valueType: 'string' },
      { key: 'source_framework_id', valueType: 'frameworkId' },
      { key: 'target_framework_id', valueType: 'frameworkId' },
      { key: 'method', valueType: 'string' },
      { key: 'status', valueType: 'crosswalkStatus' },
      { key: 'created_by', valueType: 'string' },
      { key: 'Valid Alignments', valueType: 'crosswalkAlignments' },
    ],
  },
  frameworks: {
    title: 'Frameworks',
    mutationUpdateKey: 'updateFramework',
    mutationAddKey: 'addFramework',
    mutationDeleteKey: 'deleteFramework',
    editableFields: [
      { key: 'name', valueType: 'string' },
      { key: 'jurisdiction', valueType: 'string' },
      { key: 'subject_id', valueType: 'subject' },
      { key: 'version_label', valueType: 'string' },
    ],
  },
  standardAlignments: {
    title: 'Standard Alignments',
    mutationUpdateKey: 'updateStandardAlignment',
    mutationAddKey: 'addStandardAlignment',
    mutationDeleteKey: 'deleteStandardAlignment',
    editableFields: [
      { key: 'mapping_set_id', valueType: 'number', locked: true },
      { key: 'source_standard_code', valueType: 'string' },
      { key: 'target_standard_code', valueType: 'string' },
      { key: 'alignment_type', valueType: 'string', locked: true },
      { key: 'rating', valueType: 'number' },
      { key: 'alignment_group_id', valueType: 'string', locked: true },
      { key: 'note', valueType: 'string' },
    ],
  },
} as const satisfies Record<string, editableResource | undefined>

export const updatingIdAtom = atom<number, [number], void>(
  (get) => {
    return get(updatingIdAtomInternal)
  },
  (_, set, newVal) => {
    set(updatingIdAtomInternal, newVal)
    set(updateMapAtom, {})
  },
)

export type ResourceKey = keyof typeof resourceUpdateMap

export const resourceKeyAtom = atom<ResourceKey, [ResourceKey], void>(
  (get) => {
    return get(resourceKeyInternalAtom)
  },
  (_, set, newVal) => {
    set(resourceKeyInternalAtom, newVal)
    set(updatingIdAtom, 0)
  },
)

export const isAddingAtom = atom<boolean, [boolean], void>(
  (get) => {
    return get(isAddingAtomInternal)
  },
  (_, set, newVal) => {
    set(isAddingAtomInternal, newVal)
    set(updatingIdAtom, 0)
  },
)

export const listDataAtom = atomWithRefresh(async (get) => {
  const resourceKey = get(resourceKeyAtom)
  return fetchUnPaginatedResourceList(resourceKey)
})

export const listDataTitleAtom = atom((get) => {
  const resourceKey = get(resourceKeyAtom)
  return resourceUpdateMap[resourceKey]?.title
})

export const mutationUpdateKeyAtom = atom((get) => {
  const resourceKey = get(resourceKeyAtom)
  return resourceUpdateMap[resourceKey]?.mutationUpdateKey
})

export const mutationAddKeyAtom = atom((get) => {
  const resourceKey = get(resourceKeyAtom)
  return resourceUpdateMap[resourceKey]?.mutationAddKey
})

export const disableEditAtom = atom((get) => {
  const resourceKey = get(resourceKeyAtom)
  return (
    (resourceUpdateMap[resourceKey] as editableResource)?.disableUpdates ||
    false
  )
})

export const mutationDeleteKeyAtom = atom((get) => {
  const resourceKey = get(resourceKeyAtom)
  return resourceUpdateMap[resourceKey]?.mutationDeleteKey
})

export const mutationFieldsAtom = atom((get) => {
  const resourceKey = get(resourceKeyAtom)
  return resourceUpdateMap[resourceKey]?.editableFields
})

export const addingDisabledAtom = atom((get) => {
  return !get(mutationAddKeyAtom)
})
